// frontend/src/components/admin/AdminLogin.js
import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Mail, Lock, Eye, EyeOff, ShieldCheck, LogIn } from 'lucide-react';
import MetaData from '../layouts/MetaData';
import { loginAdmin, clearAdminError } from '../../actions/adminActions';
import Loader from '../layouts/Loader';

const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const { loading, isAuthenticated, error } = useSelector(state => state.adminState);

    useEffect(() => {
        if (!isAuthenticated) {
            localStorage.removeItem('adminInfo');
        }
        if (isAuthenticated) {
            navigate('/admin/dashboard');
        }
        if (error) {
            toast.error(error);
            dispatch(clearAdminError());
        }
    }, [isAuthenticated, error, navigate, dispatch]);

    const submitHandler = async (e) => {
        e.preventDefault();
        try {
            const success = await dispatch(loginAdmin(email, password));
            if (success) {
                toast.success('Login successful');
                navigate('/admin/dashboard');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Invalid login credentials');
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <MetaData title="Admin Login" />
            <div 
                className="min-h-screen flex items-center justify-center py-8"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-md w-full mx-4 bg-white bg-opacity-95 p-6 rounded-lg shadow-sm">
                    <div className="text-center mb-4">
                        <div className="flex items-center justify-center mb-2">
                            <ShieldCheck className="h-8 w-8 text-[#dc2626]" />
                        </div>
                        <h1 className="text-2xl font-bold text-[#dc2626] mb-1 font-['Crimson_Text']">Admin Portal</h1>
                        <p className="text-sm text-gray-700">Enter your credentials to login</p>
                    </div>

                    <form className="space-y-4" onSubmit={submitHandler}>
                        <div className="space-y-3">
                            {/* Email Field */}
                            <div>
                                <label htmlFor="email_field" className="block text-xs font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-[#dc2626]" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email_field"
                                        className="h-10 appearance-none block w-full pl-10 pr-3 py-2 text-center border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors duration-300 text-sm placeholder:text-center"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label htmlFor="password_field" className="block text-xs font-medium text-gray-700 mb-1">
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-4 w-4 text-[#dc2626]" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password_field"
                                        className="h-10 appearance-none block w-full pl-10 pr-10 py-2 text-center border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors duration-300 text-sm placeholder:text-center"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? 
                                            <EyeOff className="h-4 w-4 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-200" /> :
                                            <Eye className="h-4 w-4 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-200" />
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <button
                                type="submit"
                                className="group relative w-full h-10 flex justify-center items-center px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Authenticating...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        <LogIn className="h-4 w-4 mr-2" />
                                        Login to Dashboard
                                    </span>
                                )}
                            </button>
                        </div>

                        <div className="relative mt-3">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 text-gray-500 bg-white rounded-md">
                                    Admin Access Only
                                </span>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Fragment>
    );
};

export default AdminLogin;